import {
  AttendeeHealthBoxHistoryPage,
  HealthBoxInfoPageResponse,
  HealthBoxInfoView,
  HealthboxCreateData,
} from "@/types";
import { apiUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { IHealthboxService } from "./IHealthboxService";

export class HealthboxService implements IHealthboxService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async getAll(
    page: number = 0,
    size: number = 10,
  ): Promise<HealthBoxInfoPageResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined && page !== null) {
        queryParams.append("page", page.toString());
      }
      if (size !== undefined && size !== null) {
        queryParams.append("size", size.toString());
      }

      const queryString = queryParams.toString();
      const endpoint = `/healthBox${queryString ? `?${queryString}` : ""}`;

      return await this.httpClient.get<HealthBoxInfoPageResponse>(endpoint);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os healthboxes. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getById(id: number): Promise<HealthBoxInfoView> {
    try {
      const response = await this.httpClient.get<
        Omit<HealthBoxInfoView, "id"> & { id?: number }
      >(`/healthBox/${id}`);

      return { ...response, id };
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os healthboxes. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async search(query: string): Promise<HealthBoxInfoView[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("HealthBox name or sku value", query);
      const endpoint = `/healthBox/search?${queryParams.toString()}`;
      return await this.httpClient.get<HealthBoxInfoView[]>(endpoint);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os healthboxes. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async create(data: HealthboxCreateData): Promise<void> {
    try {
      await this.httpClient.post<void>("/healthBox/create", data);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível criar a healthbox. Confirme os SKUs, itens obrigatórios e stock.",
      );
      throw new Error(errorMessage);
    }
  }

  async update(id: number, data: HealthboxCreateData): Promise<void> {
    try {
      await this.httpClient.put<void>(`/healthBox/${id}`, data);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível criar a healthbox. Confirme os SKUs, itens obrigatórios e stock.",
      );
      throw new Error(errorMessage);
    }
  }

  async getByAttendeeId(
    attendeeId: number,
    onlyPurchased: boolean,
    page: number,
    size: number,
  ): Promise<AttendeeHealthBoxHistoryPage> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("onlyPurchased", String(onlyPurchased));
      queryParams.append("page", String(page));
      queryParams.append("size", String(size));
      const endpoint = `/healthBox/attendee/${attendeeId}?${queryParams.toString()}`;
      return await this.httpClient.get<AttendeeHealthBoxHistoryPage>(endpoint);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os healthboxes. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }
}
